import * as React from "react"
import { ProductImage } from "./product-image"
import { ProductSelectMenu } from "./product-select-menu"
import { ProductVariant } from "./types"
import { ProductPrice } from "./product-price"
import { ProductConfirmButtons } from "./product-confirm-buttons"
import { useRecoilValue, useSetRecoilState } from "recoil"
import {
  brandingSelector,
  productCheckoutPageSelector
} from "../../../../recoil/atoms/room"
import { pipParamsSelector } from "../../../../recoil/atoms/pictureInPicture"
import useWindowSize from "../../../../hooks/useWindowSize"
import { useResizeObserver } from "../../../../hooks/useResizeObserver"
import { useWatchParty } from "../../../../hooks/useWatchParty"
import {
  ModalProductContainer,
  ImageProductContainer,
  SmallContainerProductPrice,
  SelectContainer,
  ConfirmButtonsContainer
} from "./styles"
import { productOverlaySelector } from "../../../../recoil/atoms/product-overlay"
import { viewDependingLayoutAtom } from "../../../../recoil/atoms/view"

interface ModalOpenProductOverlayProps {
  isSmallScreen: boolean
  isLargeScreen: boolean
  containerStyles: React.CSSProperties
  isHistoryProduct: boolean
}

const _ModalOpenProductOverlay: React.FC<ModalOpenProductOverlayProps> = ({
  containerStyles,
  isLargeScreen,
  isSmallScreen,
  isHistoryProduct
}) => {
  const productOverlayContainerRef = React.useRef<HTMLDivElement>(null)
  const imagesContainerRef = React.useRef<HTMLDivElement>(null)
  const smallPriceProductContainerRef = React.useRef<HTMLDivElement>(null)
  const buttonsContainerRef = React.useRef<HTMLDivElement>(null)
  const productSelectContainerRef = React.useRef<HTMLDivElement>(null)
  const largeOptionsContainer = React.useRef<HTMLDivElement>(null)

  const setPiPParams = useSetRecoilState(pipParamsSelector)

  const { isLandscape, isMobile, isTablet } = useRecoilValue(
    viewDependingLayoutAtom
  )
  const {
    productTitle,
    options,
    variants,
    defaultImageSrc,
    coverImages,
    isOpen,
    selectedImages
  } = useRecoilValue(productOverlaySelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const isProductCheckoutPage = useRecoilValue(productCheckoutPageSelector)

  const [selectedQuantity, setSelectedQuantity] = React.useState<number>(1)
  const [isLargeDropDown, setLargeDropDown] = React.useState<boolean>(false)
  const [selectedVariant, setSelectedVariant] = React.useState<ProductVariant>(
    {}
  )

  const defualtOptionsSelected = React.useMemo(() => {
    return variants.length !== 0
      ? variants[0].option_values.reduce((acc, el) => {
          acc[`${el.option_id}`] = el
          return acc
        }, {})
      : {}
  }, [variants])

  const [optionsSelected, setOptionsSelected] = React.useState(
    defualtOptionsSelected
  )

  const { screenSize } = useWindowSize()
  const { isWatchParty } = useWatchParty()
  const [widthImagesContainer, heightImagesContainer] =
    useResizeObserver(imagesContainerRef)
  const [, smallPriceProductContainerHeight] = useResizeObserver(
    smallPriceProductContainerRef
  )
  const [, buttonsContainerHeight] = useResizeObserver(buttonsContainerRef)
  const [widthProductOverlayContainer] = useResizeObserver(
    productOverlayContainerRef
  )

  const isProductWithoutOptions = React.useMemo<boolean>(() => {
    return !options
      ? true
      : options?.length === 1 && options?.[0]?.values[0] === "Default Title"
  }, [options])

  const isSmallElementProductPrice = React.useMemo<boolean>(() => {
    return isSmallScreen && !isProductWithoutOptions && !isProductCheckoutPage
  }, [isProductCheckoutPage, isProductWithoutOptions, isSmallScreen])

  const heightImages = React.useMemo<number>(() => {
    return isSmallScreen && !isProductWithoutOptions
      ? heightImagesContainer - smallPriceProductContainerHeight
      : heightImagesContainer
  }, [
    heightImagesContainer,
    isProductWithoutOptions,
    isSmallScreen,
    smallPriceProductContainerHeight
  ])

  const defualtVariant = React.useMemo(
    () => (variants ? variants[0] : {}),
    [variants]
  )

  const isBigContent =
    productSelectContainerRef?.current?.scrollWidth - 20 <
    largeOptionsContainer?.current?.scrollWidth

  /*  React.useEffect(() => {
    setSelectedVariant(defualtVariant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) */

  React.useEffect(() => {
    // variant array updated - update the selected variant detail
    setSelectedVariant(prev => {
      if (!prev) return prev
      let item = variants.find(variant => variant.id === prev.id)

      // if variant is deleted, then first variant is selected.
      if (!item) item = variants[0]

      // Update the option selected
      const selected =
        variants.length !== 0
          ? item.option_values.reduce((acc, el) => {
              acc[`${el.option_id}`] = el
              return acc
            }, {})
          : {}
      setOptionsSelected(selected)

      return item
    })
  }, [variants])

  React.useEffect(() => {
    if (isHistoryProduct) {
      setSelectedVariant(defualtVariant)
    }
  }, [isHistoryProduct, defualtVariant])

  React.useEffect(() => {
    setSelectedVariant(defualtVariant)
    setSelectedQuantity(1)
  }, [defualtVariant])

  React.useEffect(() => {
    if (/* isOpen && !isBadgeLiveMode &&  */ coverImages?.length !== 0) {
      const image = coverImages?.find(image => {
        return image?.variant_ids?.find(vId => vId === selectedVariant?.id)
      })

      setPiPParams({
        coverImage: image ? image.src : defaultImageSrc
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, coverImages])

  React.useLayoutEffect(() => {
    if (
      isBigContent &&
      productSelectContainerRef?.current?.scrollWidth !== undefined &&
      largeOptionsContainer?.current?.scrollWidth !== undefined &&
      productSelectContainerRef?.current?.scrollWidth !== 0 &&
      largeOptionsContainer?.current?.scrollWidth !== 0 &&
      isLargeScreen &&
      screenSize.width < 1500
    ) {
      setLargeDropDown(true)
    }
  }, [
    isBigContent,
    isLargeScreen,
    productSelectContainerRef?.current?.scrollWidth,
    screenSize.width,
    widthProductOverlayContainer
  ])

  return (
    <ModalProductContainer
      id="product-overlay-container"
      ref={productOverlayContainerRef}
      roomStyles={roomStyles}
      isMobile={isMobile}
      isTablet={isTablet}
      isLandscape={isLandscape}
      style={{ ...containerStyles }}
    >
      <ImageProductContainer
        id="product-images-container"
        ref={imagesContainerRef}
        isSmallScreen={isSmallScreen}
        isLargeScreen={isLargeScreen}
        isLandscape={isLandscape}
        isMobile={isMobile}
      >
        <ProductImage
          keyForImage={`images-${productTitle}`}
          selectedImages={selectedImages}
          width={widthImagesContainer}
          height={heightImages}
          isSmallScreen={isSmallScreen}
        />
        <SmallContainerProductPrice ref={smallPriceProductContainerRef}>
          {isSmallElementProductPrice && (
            <ProductPrice
              isProductWithoutOptions={isProductWithoutOptions}
              setSelectedQuantity={setSelectedQuantity}
              selectedVariant={selectedVariant}
              selectedQuantity={selectedQuantity}
              isSmallScreen={isSmallScreen}
            />
          )}
        </SmallContainerProductPrice>
      </ImageProductContainer>

      <SelectContainer
        id="product-select-container"
        ref={productSelectContainerRef}
        isLandscape={isLandscape}
        isLargeScreen={isLargeScreen}
        isMobile={isMobile}
        isSmallScreen={isSmallScreen}
        widthImagesContainer={widthImagesContainer}
        buttonsContainerHeight={buttonsContainerHeight}
      >
        <ProductSelectMenu
          setOptionsSelected={setOptionsSelected}
          optionsSelected={optionsSelected}
          setSelectedQuantity={setSelectedQuantity}
          selectedQuantity={selectedQuantity}
          setSelectedVariant={setSelectedVariant}
          isLargeDropDown={isLargeDropDown}
          largeOptionsContainer={largeOptionsContainer}
          isLargeScreen={isLargeScreen}
          isSmallScreen={isSmallScreen}
          selectedVariant={selectedVariant}
          isProductWithoutOptions={isProductWithoutOptions}
        />
      </SelectContainer>
      <ConfirmButtonsContainer
        isLandscape={isLandscape}
        isMobile={isMobile}
        isTablet={isTablet}
        isWatchParty={isWatchParty}
      >
        <ProductConfirmButtons
          isHistoryProduct={isHistoryProduct}
          setSelectedVariant={setSelectedVariant}
          setSelectedQuantity={setSelectedQuantity}
          setOptionsSelected={setOptionsSelected}
          setLargeDropDown={setLargeDropDown}
          buttonsContainerRef={buttonsContainerRef}
          selectedVariant={selectedVariant}
          isSmallScreen={isSmallScreen}
          selectedQuantity={selectedQuantity}
          defualtOptionsSelected={defualtOptionsSelected}
          defualtVariant={variants ? variants[0] : {}}
        />
      </ConfirmButtonsContainer>
    </ModalProductContainer>
  )
}

export const ModalOpenProductOverlay = React.memo(_ModalOpenProductOverlay)
